import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useLazyQuery } from '@/hooks/useLazyQuery';
import { useQueryStable } from '@/hooks/useQueryStable';

export const GetAvailableDataPlatforms = graphql(/* GraphQL */ `
  query AvailableDataPlatforms($accountId: BigInt!) {
    catalog(accountId: $accountId) {
      dataPlatform {
        availablePlatformTypes
      }
    }
  }
`);

export type AvailableDataPlatformsParams = {
  pause?: boolean;
};

export const useAvailableDataPlatforms = ({ pause }: AvailableDataPlatformsParams) => {
  const { accountId, hasAccountFeature } = useDbtCloudAccount();
  const shouldFetchWarehouseData =
    hasAccountFeature('catalogIngestion') && hasAccountFeature('explorerAccountUI');

  const [{ fetching, error, data }] = useQueryStable({
    query: GetAvailableDataPlatforms,
    variables: {
      accountId: accountId ?? 0,
    },
    pause: pause || !accountId || !shouldFetchWarehouseData,
  });

  const query = useMemo(() => {
    return {
      fetching,
      error,
      data: data?.catalog.dataPlatform.availablePlatformTypes,
    };
  }, [fetching, error, data]);

  return query;
};

export const useLazyAvailablePlatforms = () => {
  const [catalogEntity, queryExecute] = useLazyQuery(GetAvailableDataPlatforms);

  const executeQuery = async (args: { accountId: number }) => {
    const res = await queryExecute({
      accountId: args.accountId ?? 0,
    });
    return {
      ...res,
      data: res.data?.catalog.dataPlatform.availablePlatformTypes,
      fetching: false,
    };
  };

  return [
    {
      ...catalogEntity,
      data: catalogEntity.data?.catalog.dataPlatform.availablePlatformTypes,
    },
    executeQuery,
  ] as const;
};
